import * as React from 'react'
import { v4 as uuid } from 'uuid';
import { renderToString, } from 'react-dom/server'

import { ESCAPE, } from '../../../Shared'
import { onMobile } from '../../libs/onMobile';

interface TooltipProps {
  averageItemHeight?: number;
  handleClick?: (e: any) => void;
  isTabbable?: boolean;
  mode?: string;
  tooltipText: string | Array<string>;
  tooltipTriggerStyle?: { [key: string]: any };
  tooltipTriggerText: string | JSX.Element | JSX.Element[] | string[];
  tooltipTriggerTextClass?: string;
  tooltipTriggerTextStyle?: { [key: string]: any };
}

const VISIBLE = 'visible';
const RIGHT_JUSTIFY = 'right-justify';

class Tooltip extends React.Component<TooltipProps, { clickedFromMobile: boolean; tooltipVisible: boolean }> {
  private tooltip: any;  // eslint-disable-line react/sort-comp
  private tooltipContent: any;  // eslint-disable-line react/sort-comp
  private tooltipTrigger: any;  // eslint-disable-line react/sort-comp
  private tooltipWrapper: any;  // eslint-disable-line react/sort-comp
  private tooltipId: string;  // eslint-disable-line react/sort-comp
  private tooltipContentId: string;  // eslint-disable-line react/sort-comp

  constructor(props) {
    super(props);
    // Generate a unique id for the tooltip so it can be referenced.
    this.tooltipId = `tooltip-${uuid()}`;
    this.tooltipContentId = `tooltip-content-${uuid()}`

    document.addEventListener('click', this.handlePageClick.bind(this));
    document.addEventListener('keydown', this.handleKeyDown.bind(this));

    this.state = { clickedFromMobile: false, tooltipVisible: false };

    this.showTooltip = this.showTooltip.bind(this);
    this.hideTooltip = this.hideTooltip.bind(this);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handlePageClick, false);
    document.removeEventListener('keydown', this.handleKeyDown, false);
  }

  showTooltip() {
    if (this.tooltip.classList.value.includes(VISIBLE)) { return; }

    const { tooltipText, averageItemHeight } = this.props;
    const activeTooltips = document.getElementsByClassName('visible quill-tooltip');
    Array.from(activeTooltips).forEach(tooltip => tooltip.classList.remove('visible'));

    let textToShow = tooltipText;

    if (Array.isArray(tooltipText)) {
      const availableSpace = window.innerHeight - this.tooltip.getBoundingClientRect().top;

      if (averageItemHeight && (tooltipText.length * averageItemHeight >= availableSpace)) {
        const itemsToFit = Math.floor(availableSpace / averageItemHeight) - 1;
        const remainingItems = tooltipText.length - itemsToFit;
        textToShow = `${tooltipText.slice(0, itemsToFit).map(t => `<span>${t}</span>`).join('<br/><br/>')}<br/><br/><b>...and ${remainingItems} more</b>`;
      } else {
        textToShow = tooltipText.join('<br/>');
      }
    }

    this.tooltipContent.innerHTML = textToShow;
    this.tooltip.classList.add(VISIBLE);

    if (this.tooltip.getBoundingClientRect().right >= window.innerWidth) {
      this.tooltipWrapper.classList.add(RIGHT_JUSTIFY);
    }
  }

  hideTooltip() {
    this.tooltip.classList.remove(VISIBLE);
    this.tooltipWrapper.classList.remove(RIGHT_JUSTIFY);
    this.tooltipContent.innerHTML = '';
    this.setState({ tooltipVisible: false });
  }

  handlePageClick = (e) => {
    const { tooltipTriggerTextClass, handleClick } = this.props;
    const clickedOutsideTooltip = this.tooltip && e.target !== this.tooltip && e.target !== this.tooltipTrigger && e.target.className !== tooltipTriggerTextClass;
    if (clickedOutsideTooltip) {
      handleClick && handleClick(e);
      this.hideTooltip();
    } else {
      this.handleTooltipClick(e);
    }
  }

  handleTooltipClick = (e) => {
    if (e.nativeEvent) {
      const { tooltipVisible } = this.state;

      if (!onMobile()) {
        return () => false;
      }
      if (tooltipVisible) {
        this.setState({ tooltipVisible: false });
        this.hideTooltip();
      } else {
        this.setState({ tooltipVisible: true });
        this.showTooltip();
        this.hideTooltip();
      }
    }
  }

  handleKeyDown = (e) => {
    if (e.key === ESCAPE) { this.hideTooltip(); }
  }

  render() {
    const {
      isTabbable,
      mode,
      tooltipTriggerStyle,
      tooltipTriggerText,
      tooltipTriggerTextClass,
      tooltipTriggerTextStyle,
      tooltipText,
    } = this.props;
    const { tooltipVisible } = this.state;
    const tabIndex = isTabbable ? 0 : undefined;
    const focusClass = mode && mode === 'dark' ? 'focus-on-dark' : 'focus-on-light';
    const triggerClass = `quill-tooltip-trigger ${focusClass} ${tooltipVisible ? 'active' : ''}`;

    // there are some places, like in data tables, where the text is partially hidden using ellipses in CSS but is still available in the DOM
    // so screenreaders don't need to be alerted because it's redundant
    const hideForScreenreaders = tooltipText === tooltipTriggerText

    return (
      <span
        className={triggerClass}
        onBlur={this.hideTooltip}
        onClick={this.handleTooltipClick}
        onFocus={this.showTooltip}
        onMouseEnter={this.showTooltip}
        onMouseLeave={this.hideTooltip}
        ref={node => this.tooltipTrigger = node}
        style={tooltipTriggerStyle}
        tabIndex={tabIndex}
      >
        <span
          aria-describedby={this.tooltipId}
          className={tooltipTriggerTextClass}
          style={tooltipTriggerTextStyle}
        >
          {tooltipTriggerText}
        </span>
        <span
          className="quill-tooltip-wrapper"
          ref={node => this.tooltipWrapper = node}
        >
          <span
            aria-hidden={hideForScreenreaders}
            aria-labelledby={tooltipVisible ? this.tooltipContentId : null}
            className="quill-tooltip"
            id={this.tooltipId}
            ref={node => this.tooltip = node}
            role={tooltipVisible ? "tooltip" : null}
          >
            <span id={this.tooltipContentId} ref={node => this.tooltipContent = node} />
          </span>
        </span>
      </span>
    );
  }
}

Tooltip.defaultProps = {
  isTabbable: true,
};

export { Tooltip, }
